import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { atom } from 'jotai/vanilla';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import type { GroupId, ViewModelId } from 'balus-common';
import { ViewModelFileStorageLimitBytes } from 'balus-common';
import { Loading } from '@framework/ui/atoms/Loading';
import { classNames } from '@framework/utils';
import { formatFileSize } from '@framework/utils/formatFileSize';
import { useGroupLicensePermissions } from '@group/License/useGroupLicensePermissions';
import { viewModelPageContentAtom } from '@user/pages/ViewModelPage/viewModelPageContentAtom';
import { ViewCollection } from '@view-model/domain/view/ViewCollection';
import { SortOrderMenu } from '../SortOrderMenu';
import type { SortOrder } from '../SortOrderMenu';
import { FileListItem } from './FileListItem';
import { useFetchFileContainers } from './useFetchFileContainers';

type Props = {
  groupId: GroupId;
  viewModelId: ViewModelId;
  canUploadFile: boolean;
  onClose: () => void;
};

export const FileList: React.FC<Props> = ({ groupId, viewModelId, canUploadFile, onClose }: Props) => {
  const { t } = useTranslation();
  const licensePermissions = useGroupLicensePermissions();
  const viewsAtom = useMemo(
    () =>
      atom(
        (get) =>
          get(viewModelPageContentAtom).viewContentsRecord[viewModelId]?.getViews() ?? ViewCollection.buildEmpty()
      ),
    [viewModelId]
  );
  const views = useAtomValue(viewsAtom);
  const { items, totalFileSize, firstLoading } = useFetchFileContainers(viewModelId, views);
  const [sortOrder, setSortOrder] = useState<SortOrder>('newest');
  const sortedItems = useMemo(() => (sortOrder === 'oldest' ? items.slice().reverse() : items), [items, sortOrder]);

  return (
    <div className="pointer-events-auto flex max-h-full w-[280px] flex-col overflow-hidden rounded-lg bg-white">
      <div className="flex items-center justify-between px-4 py-3">
        <span className="text-base font-bold">{t('viewModel.fileList.title')}</span>
        <button
          type="button"
          aria-label={t('common.action.close')}
          onClick={onClose}
          className="flex size-6 items-center justify-center rounded text-gray-500 hover:bg-gray-100"
        >
          <FontAwesomeIcon icon={faXmark} aria-hidden />
        </button>
      </div>
      <div className="px-4 pb-2">
        <SortOrderMenu
          menuId="file-list-sort-menu"
          sortOrder={sortOrder}
          labels={{ newest: t('viewModel.fileList.sortNewest'), oldest: t('viewModel.fileList.sortOldest') }}
          onSortChange={setSortOrder}
        />
      </div>

      {firstLoading ? (
        <div className="p-4">
          <Loading />
        </div>
      ) : items.length === 0 ? (
        <div className="px-4 py-6 text-center">
          <div className="text-sm font-bold">{t('viewModel.fileList.empty.title')}</div>
          {canUploadFile && (
            <div className="mt-1 text-xs text-gray-600">
              {licensePermissions && !licensePermissions.fileUpload
                ? t('viewModel.fileList.empty.messageLicenseRequired')
                : t('viewModel.fileList.empty.message')}
            </div>
          )}
        </div>
      ) : (
        <div className="overflow-y-scroll [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
          {sortedItems.map((item) => (
            <FileListItem key={item.container.id} item={item} groupId={groupId} viewModelId={viewModelId} />
          ))}
        </div>
      )}

      <FileUsage totalFileSize={totalFileSize} />
    </div>
  );
};

type FileUsageProps = {
  totalFileSize: number;
};

const FileUsage: React.FC<FileUsageProps> = ({ totalFileSize }: FileUsageProps) => {
  const { t } = useTranslation();
  const isOverLimit = totalFileSize > ViewModelFileStorageLimitBytes;
  const ratio = Math.min(1, totalFileSize / ViewModelFileStorageLimitBytes);
  const barColor = isOverLimit ? 'bg-red-500' : ratio >= 0.8 ? 'bg-yellow-500' : 'bg-brand';

  return (
    <div className="border-t border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between text-xs text-gray-600">
        <span>{t('viewModel.fileList.usage.label')}</span>
        <span>
          {formatFileSize(totalFileSize)} / {ViewModelFileStorageLimitBytes / 1024 ** 3}GB
        </span>
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-gray-200">
        <div className={classNames('h-full rounded-full', barColor)} style={{ width: `${ratio * 100}%` }} />
      </div>
      {isOverLimit && <p className="mt-1.5 text-xs text-red-600">{t('viewModel.fileList.usage.overLimit')}</p>}
    </div>
  );
};
